import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { QRCodeCanvas } from 'qrcode.react';
import { Copy, Mail, Phone, Check, Loader2, Link2, CheckCircle, AlertTriangle, QrCode, Download } from 'lucide-react';

/**
 * Share a user's Quick Login link via Copy / Email / SMS.
 * Email & SMS channels are gated by isNotificationChannelActive() which combines
 * the feature flag + user privilege + the customer's notification panel setting,
 * exactly like Share Position (Live Tracking) and Share Evidence (Media).
 */
export const ShareQuickLoginModal = ({ open, onOpenChange, user, userId, quickLoginUrl, api }) => {
  const { isNotificationChannelActive } = useFeatureFlags();
  const [tab, setTab] = useState('link');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [smsRecipient, setSmsRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [alert, setAlert] = useState(null); // {success, message}

  const canEmail = isNotificationChannelActive('notification_email');
  const canSMS = isNotificationChannelActive('notification_sms');

  // Pre-fill recipients from the target user's record each time the modal opens.
  useEffect(() => {
    if (open) {
      setTab('link');
      setEmailRecipient(user?.email || '');
      setSmsRecipient(user?.mobile || user?.phone || '');
      setMessage('');
      setAlert(null);
      setCopied(false);
    }
  }, [open, user]);

  const handleCopyLink = async () => {
    if (!quickLoginUrl) { setAlert({ success: false, message: 'No login link available' }); return; }
    try {
      await navigator.clipboard.writeText(quickLoginUrl);
      setCopied(true);
      setAlert({ success: true, message: 'Quick login link copied to clipboard' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setAlert({ success: false, message: 'Failed to copy link' });
    }
  };

  const handleSend = async () => {
    const recipient = (tab === 'email' ? emailRecipient : smsRecipient).trim();
    if (!recipient) {
      setAlert({ success: false, message: tab === 'email' ? 'Please enter an email address' : 'Please enter a phone number' });
      return;
    }
    setSending(true);
    setAlert(null);
    try {
      await api.post(`/users/${userId}/share-quick-login`, {
        method: tab,
        recipient,
        message: message.trim(),
      });
      setAlert({ success: true, message: `Quick login link sent via ${tab === 'email' ? 'email' : 'SMS'} to ${recipient}` });
      setMessage('');
    } catch (err) {
      setAlert({ success: false, message: err.response?.data?.detail || `Failed to share via ${tab}` });
    } finally {
      setSending(false);
    }
  };

  const handleClose = (v) => {
    if (!v) { setAlert(null); setMessage(''); }
    onOpenChange(v);
  };

  const handleDownloadQR = () => {
    const canvas = document.querySelector('#quick-login-qr-canvas canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    const name = (user?.full_name || user?.email || 'user').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    link.download = `quick-login-qr-${name}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const tabs = [{ id: 'link', label: 'Copy', icon: Copy }, { id: 'qr', label: 'QR Code', icon: QrCode }];
  if (canEmail) tabs.push({ id: 'email', label: 'Email', icon: Mail });
  if (canSMS) tabs.push({ id: 'sms', label: 'SMS', icon: Phone });

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="share-quick-login-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#2563eb]" />
            Share Quick Login Link
          </DialogTitle>
        </DialogHeader>

        <div className="p-3 rounded-lg bg-muted/50 border text-sm space-y-1">
          <p className="font-semibold">{user?.full_name || user?.email || 'User'}</p>
          {user?.email && <p className="text-xs text-muted-foreground">{user.email}</p>}
          <p className="text-xs text-amber-600">⚠️ Anyone with this link can log in as this user.</p>
        </div>

        <div className="flex gap-1 border-b">
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setAlert(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                tab === t.id ? 'border-b-2 border-[#2563eb] text-[#2563eb]' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`share-quick-login-tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        <div className="h-[330px] overflow-y-auto" data-testid="share-quick-login-tab-content">
        {tab === 'link' && (
          <div className="flex gap-2">
            <Input value={quickLoginUrl || ''} readOnly className="text-xs bg-muted font-mono" data-testid="share-quick-login-link-input" />
            <Button onClick={handleCopyLink} disabled={!quickLoginUrl} className="shrink-0" data-testid="share-quick-login-copy-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        )}

        {tab === 'qr' && (
          <div className="flex flex-col items-center gap-3" data-testid="share-quick-login-qr-panel">
            {quickLoginUrl ? (
              <>
                <div id="quick-login-qr-canvas" className="p-3 bg-white rounded-lg border" data-testid="share-quick-login-qr-code">
                  <QRCodeCanvas value={quickLoginUrl} size={200} level="M" includeMargin={false} />
                </div>
                <p className="text-xs text-muted-foreground text-center">Scan this code with a phone camera to log in instantly as this user.</p>
                <Button onClick={handleDownloadQR} variant="outline" className="w-full" data-testid="share-quick-login-qr-download-btn">
                  <Download className="w-4 h-4 mr-2" />Download QR Code
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-6">No login link available</p>
            )}
          </div>
        )}

        {tab === 'email' && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Recipient Email</Label>
              <Input type="email" value={emailRecipient} onChange={e => setEmailRecipient(e.target.value)} placeholder="email@example.com" data-testid="share-quick-login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Message (optional)</Label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Add a note..." rows={2} className="text-sm" data-testid="share-quick-login-email-message" />
            </div>
            <Button onClick={handleSend} disabled={sending} className="w-full" data-testid="share-quick-login-email-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </div>
        )}

        {tab === 'sms' && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Recipient Phone Number</Label>
              <Input type="tel" value={smsRecipient} onChange={e => setSmsRecipient(e.target.value)} placeholder="+61 400 000 000" data-testid="share-quick-login-sms-input" />
            </div>
            <Button onClick={handleSend} disabled={sending} className="w-full" data-testid="share-quick-login-sms-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
              Send SMS
            </Button>
          </div>
        )}

        {alert && (
          <div className={`mt-3 p-3 rounded-lg border text-center ${alert.success ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center justify-center gap-2">
              {alert.success ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
              <p className="text-sm font-medium">{alert.message}</p>
            </div>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareQuickLoginModal;
